import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CheckoutStep = "billing" | "shipping" | "appointment" | "payment";

export interface CheckoutState {
  currentStep: CheckoutStep;
  selectedPaymentMethod: string | null;
  isProcessing: boolean;
  serverErrors: Record<string, string> | null;
  useBillingForShipping: boolean;
  customerNote: string;
  isMailingSubscribed: boolean;
  pluxeeGsm: string;
  pluxeeOtp: string;
  appointmentDate: string;
  appointmentTime: string;
}

const getInitialStep = (): CheckoutStep => {
  const params = new URLSearchParams(window.location.search);
  const step = params.get("step");
  const validSteps: CheckoutStep[] = [
    "billing",
    "shipping",
    "appointment",
    "payment",
  ];
  return validSteps.includes(step as CheckoutStep)
    ? (step as CheckoutStep)
    : "billing";
};

const initialState: CheckoutState = {
  currentStep: getInitialStep(),
  selectedPaymentMethod: null,
  isProcessing: false,
  serverErrors: null,
  useBillingForShipping: true,
  customerNote: "",
  isMailingSubscribed: false,
  pluxeeGsm: "",
  pluxeeOtp: "",
  appointmentDate: "",
  appointmentTime: "",
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.currentStep = action.payload;
    },
    setSelectedPaymentMethod: (state, action: PayloadAction<string | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    setUseBillingForShipping: (state, action: PayloadAction<boolean>) => {
      state.useBillingForShipping = action.payload;
    },
    setCustomerNote: (state, action: PayloadAction<string>) => {
      state.customerNote = action.payload;
    },
    setIsMailingSubscribed: (state, action: PayloadAction<boolean>) => {
      state.isMailingSubscribed = action.payload;
    },
    setPluxeeGsm: (state, action: PayloadAction<string>) => {
      state.pluxeeGsm = action.payload;
    },
    setPluxeeOtp: (state, action: PayloadAction<string>) => {
      state.pluxeeOtp = action.payload;
    },
    setAppointmentDate: (state, action: PayloadAction<string>) => {
      state.appointmentDate = action.payload;
    },
    setAppointmentTime: (state, action: PayloadAction<string>) => {
      state.appointmentTime = action.payload;
    },
  },
});

export const {
  setCurrentStep,
  setSelectedPaymentMethod,
  setUseBillingForShipping,
  setCustomerNote,
  setIsMailingSubscribed,
  setPluxeeGsm,
  setPluxeeOtp,
  setAppointmentDate,
  setAppointmentTime,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
